import ReceiptIcon from "@mui/icons-material/ReceiptLongRounded";
import { Avatar, Stack, Typography } from "@mui/material";
import { blue } from "@mui/material/colors";
import StatusChip from "@entrilia/shared/components/StatusChip";
import { makeModeAwareAccent } from "@entrilia/shared/utilities/colorHelper";
import { convertISODate } from "@entrilia/shared/utilities/dateUtils";
import { formatNumber } from "@entrilia/shared/utilities/formatters";
import { stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import type { InvoiceLayoutBanner, ObjectFieldValues } from "../../../../api/types/objectTypes";
import PanelSection from "../../../common/layout/PanelSection";
import { getLookupReferenceForFieldId } from "../../../entityFields/helpers";
import type { InvoiceCardSource } from "./invoiceCardSource";

interface Props {
  banner: InvoiceLayoutBanner;
  objectData: ObjectFieldValues;
  source?: InvoiceCardSource;
}

const InvoiceBanner = ({ banner, objectData, source }: Props) => {
  const titleLookupRef = getLookupReferenceForFieldId(banner.fieldMappings.TitleField, objectData);
  const title = titleLookupRef
    ? titleLookupRef.primaryFieldValue
    : stringifyValue(objectData.fieldValues[banner.fieldMappings.TitleField]);
  const dueDate = convertISODate(stringifyValue(objectData.fieldValues[banner.fieldMappings.DueDateField]));
  const amount = objectData.fieldValues[banner.fieldMappings.AmountField];
  const currencyCode = stringifyValue(objectData.fieldValues[banner.fieldMappings.CurrencyField]);
  const details = source?.details;

  const summary = (
    <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
      <Stack direction="row" spacing={1.5}>
        <Avatar variant="rounded">
          <ReceiptIcon color="action" />
        </Avatar>

        <Stack spacing={0.5}>
          <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <Typography variant="subtitle1">{title}</Typography>
            {source && (
              <StatusChip
                label={source.label}
                tooltipText={source.description}
                color={(t) => makeModeAwareAccent(t, blue)}
                height="smaller"
              />
            )}
          </Stack>
          {dueDate && (
            <Stack direction="row" spacing={0.5}>
              <Typography variant="caption" color="textSecondary">
                Payment Due:
              </Typography>
              <Typography variant="caption">{dueDate}</Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
      {typeof amount === "number" && (
        <Typography variant="h5">
          {formatNumber(amount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currencyCode}
        </Typography>
      )}
    </Stack>
  );

  return (
    <PanelSection sx={{ p: 2 }} withTopBorder>
      {details ? (
        <Stack spacing={1.5}>
          {summary}
          <Stack spacing={0.5} sx={{ pl: 6.5 }}>
            {details}
          </Stack>
        </Stack>
      ) : (
        summary
      )}
    </PanelSection>
  );
};

export default InvoiceBanner;
